// Pure helpers (extracted to avoid babel-visual-edits-plugin call-stack issues)

export const HIGH_MULT = 1.3;
export const LOW_MULT = 0.7;

export const ACTIVITY_TYPES = [
  { id: 'commerce', label: 'Commerce' },
  { id: 'service', label: 'Service' },
  { id: 'restauration', label: 'Restauration' },
  { id: 'autre', label: 'Autre' },
];

export const MONTHS = [
  { num: 1, short: 'Jan' },
  { num: 2, short: 'Fév' },
  { num: 3, short: 'Mar' },
  { num: 4, short: 'Avr' },
  { num: 5, short: 'Mai' },
  { num: 6, short: 'Juin' },
  { num: 7, short: 'Juil' },
  { num: 8, short: 'Août' },
  { num: 9, short: 'Sep' },
  { num: 10, short: 'Oct' },
  { num: 11, short: 'Nov' },
  { num: 12, short: 'Déc' },
];

export const fmtEur = (n) => {
  const v = isFinite(n) ? n : 0;
  return v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

export const numOr0 = (v) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : 0;
};

export function weatherLabel(code) {
  if (code === undefined || code === null) return 'Inconnu';
  if (code === 0) return 'Soleil';
  if (code === 1 || code === 2) return 'Partiellement nuageux';
  if (code === 3) return 'Couvert';
  if (code === 45 || code === 48) return 'Brouillard';
  if (code === 51 || code === 53 || code === 55 || code === 56 || code === 57) return 'Bruine';
  if (code === 61 || code === 63 || code === 65 || code === 66 || code === 67 || code === 80 || code === 81 || code === 82) return 'Pluie';
  if (code === 71 || code === 73 || code === 75 || code === 77 || code === 85 || code === 86) return 'Neige';
  if (code === 95 || code === 96 || code === 99) return 'Orage';
  return 'Variable';
}

export function weatherIconClass(code) {
  if (code === 0) return 'sun text-amber-500';
  if (code === 1 || code === 2) return 'cloudsun text-amber-400';
  if (code === 3 || code === 45 || code === 48) return 'cloud text-slate-500';
  if (code === 51 || code === 53 || code === 55 || code === 56 || code === 57 ||
      code === 61 || code === 63 || code === 65 || code === 66 || code === 67 ||
      code === 80 || code === 81 || code === 82) return 'cloudrain text-sky-500';
  if (code === 71 || code === 73 || code === 75 || code === 77 || code === 85 || code === 86) return 'cloudsnow text-sky-300';
  if (code === 95 || code === 96 || code === 99) return 'cloudlightning text-violet-500';
  return 'cloudsun text-slate-400';
}

function computeSeasonalityFactor(highSeasonMonths, lowSeasonMonths) {
  const high = highSeasonMonths ? highSeasonMonths.length : 0;
  const low = lowSeasonMonths ? lowSeasonMonths.length : 0;
  const normal = Math.max(0, 12 - high - low);
  return high * HIGH_MULT + low * LOW_MULT + normal * 1.0;
}

export function computeForecast(activityType, data) {
  if (!activityType || !data) return null;

  if (activityType === 'commerce') {
    const clients = numOr0(data.monthly_clients);
    const tarif = numOr0(data.avg_price);
    const margePct = numOr0(data.margin_pct);
    const charges = numOr0(data.monthly_charges);
    const high = data.high_season_months || [];
    const low = data.low_season_months || [];

    const baseMonthlyCA = clients * tarif;
    const seasonalFactor = computeSeasonalityFactor(high, low);
    const annualCA = baseMonthlyCA * seasonalFactor;
    const grossProfit = annualCA * (margePct / 100);
    const annualCharges = charges * 12;
    const netProfit = grossProfit - annualCharges;
    const normalCount = 12 - high.length - low.length;

    const breakdown = [
      'CA mensuel de base = ' + clients + ' clients × ' + fmtEur(tarif) + ' = ' + fmtEur(baseMonthlyCA) + '/mois',
      'Saisonnalité — ' + high.length + ' mois haute (×' + HIGH_MULT + '), ' + low.length + ' mois basse (×' + LOW_MULT + '), ' + normalCount + ' mois normaux (×1,0)',
      'Coefficient annuel total = ' + seasonalFactor.toFixed(2),
      'CA annuel ajusté = ' + fmtEur(baseMonthlyCA) + ' × ' + seasonalFactor.toFixed(2) + ' = ' + fmtEur(annualCA),
      'Marge brute = ' + fmtEur(annualCA) + ' × ' + margePct + '% = ' + fmtEur(grossProfit),
      'Charges annuelles = ' + fmtEur(charges) + ' × 12 = ' + fmtEur(annualCharges),
      'Bénéfice net = ' + fmtEur(grossProfit) + ' - ' + fmtEur(annualCharges) + ' = ' + fmtEur(netProfit),
    ];

    return { annualCA, annualCharges, netProfit, breakdown };
  }

  if (activityType === 'service') {
    const clients = numOr0(data.monthly_clients);
    const tarifJournalier = numOr0(data.daily_rate);
    const joursActifs = numOr0(data.active_days_per_month);
    const charges = numOr0(data.monthly_charges);
    const high = data.high_season_months || [];
    const low = data.low_season_months || [];

    const baseMonthlyCA = joursActifs * tarifJournalier;
    const seasonalFactor = computeSeasonalityFactor(high, low);
    const annualCA = baseMonthlyCA * seasonalFactor;
    const annualCharges = charges * 12;
    const netProfit = annualCA - annualCharges;
    const avgDaysPerClient = clients > 0 ? joursActifs / clients : 0;
    const normalCount = 12 - high.length - low.length;

    const breakdown = [
      'CA mensuel de base = ' + joursActifs + ' jours actifs × ' + fmtEur(tarifJournalier) + ' = ' + fmtEur(baseMonthlyCA) + '/mois',
      'Indication : ' + avgDaysPerClient.toFixed(1) + ' jours moyens par client (' + joursActifs + ' jours / ' + clients + ' clients)',
      'Saisonnalité — ' + high.length + ' mois haute (×' + HIGH_MULT + '), ' + low.length + ' mois basse (×' + LOW_MULT + '), ' + normalCount + ' mois normaux (×1,0)',
      'Coefficient annuel total = ' + seasonalFactor.toFixed(2),
      'CA annuel ajusté = ' + fmtEur(baseMonthlyCA) + ' × ' + seasonalFactor.toFixed(2) + ' = ' + fmtEur(annualCA),
      'Charges annuelles = ' + fmtEur(charges) + ' × 12 = ' + fmtEur(annualCharges),
      'Bénéfice net = ' + fmtEur(annualCA) + ' - ' + fmtEur(annualCharges) + ' = ' + fmtEur(netProfit),
    ];

    return { annualCA, annualCharges, netProfit, breakdown };
  }

  if (activityType === 'restauration') {
    const couverts = numOr0(data.daily_covers);
    const tarif = numOr0(data.avg_price);
    const tauxRemplissage = numOr0(data.fill_rate_pct);
    const joursOuverture = numOr0(data.opening_days_per_month);
    const charges = numOr0(data.monthly_charges);

    const couvertsEffectifs = couverts * (tauxRemplissage / 100);
    const dailyCA = couvertsEffectifs * tarif;
    const monthlyCA = dailyCA * joursOuverture;
    const annualCA = monthlyCA * 12;
    const annualCharges = charges * 12;
    const netProfit = annualCA - annualCharges;

    const breakdown = [
      'Couverts effectifs/jour = ' + couverts + ' × ' + tauxRemplissage + '% = ' + couvertsEffectifs.toFixed(1) + ' couverts',
      'CA journalier = ' + couvertsEffectifs.toFixed(1) + ' couverts × ' + fmtEur(tarif) + ' = ' + fmtEur(dailyCA),
      "CA mensuel = " + fmtEur(dailyCA) + ' × ' + joursOuverture + " jours d'ouverture = " + fmtEur(monthlyCA),
      'CA annuel = ' + fmtEur(monthlyCA) + ' × 12 mois = ' + fmtEur(annualCA),
      'Charges annuelles = ' + fmtEur(charges) + ' × 12 = ' + fmtEur(annualCharges),
      'Bénéfice net = ' + fmtEur(annualCA) + ' - ' + fmtEur(annualCharges) + ' = ' + fmtEur(netProfit),
    ];

    return { annualCA, annualCharges, netProfit, breakdown };
  }

  if (activityType === 'autre') {
    const clients = numOr0(data.monthly_clients);
    const tarif = numOr0(data.avg_price);
    const joursTravailles = numOr0(data.worked_days_per_month);
    const charges = numOr0(data.monthly_charges);

    const monthlyCA = clients * tarif;
    const annualCA = monthlyCA * 12;
    const annualCharges = charges * 12;
    const netProfit = annualCA - annualCharges;
    const dailyCA = joursTravailles > 0 ? monthlyCA / joursTravailles : 0;

    const breakdown = [
      'CA mensuel = ' + clients + ' clients × ' + fmtEur(tarif) + ' = ' + fmtEur(monthlyCA),
      'Indication : CA journalier = ' + fmtEur(monthlyCA) + ' / ' + joursTravailles + ' jours travaillés = ' + fmtEur(dailyCA) + '/jour',
      'CA annuel = ' + fmtEur(monthlyCA) + ' × 12 = ' + fmtEur(annualCA),
      'Charges annuelles = ' + fmtEur(charges) + ' × 12 = ' + fmtEur(annualCharges),
      'Bénéfice net = ' + fmtEur(annualCA) + ' - ' + fmtEur(annualCharges) + ' = ' + fmtEur(netProfit),
    ];

    return { annualCA, annualCharges, netProfit, breakdown };
  }

  return null;
}
